
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function LegalServicesSection() {
  const [activeTab, setActiveTab] = useState<'before' | 'during'>('before');

  const beforeConstruction = {
    labor: [
      { title: '기본안전보건대장', detail: '발주자 (설계계약체결전)' },
      { title: '설계안전보건대장', detail: '설계자 (설계완료시점)' },
      { title: '공사안전보건대장', detail: '시공자 (착공일전)' },
      { title: '안전보건조정자선임', detail: '50억 이상 건설공사' },
      { title: '유해위험방지계획서', detail: '실착공 15일전' },
      { title: '안전보건대장적정성확인', detail: '안전보건전문가' },
    ],
    construction: [
      { title: '설계안전성평가 (D.F.S)', detail: '민간공사 제외' },
      { title: '안전관리계획서(총괄)', detail: '승인 후 착공' },
      { title: '소규모안전관리계획서', detail: '승인 후 착공' },
      { title: '지하안전영향평가', detail: '소규모지하안전영향평가' },
    ],
  };

  const duringConstruction = {
    labor: [
      { title: '유해위험방지계획서 이행실태점검', detail: '안전보건공단' },
      { title: '공사안전보건대장 이행점검', detail: '발주자' },
    ],
    construction: [
      { title: '안전관리계획서(공종별)', detail: '공종별 작성' },
      { title: '안전관리계획서상 안전점검', detail: '정기 점검' },
      { title: '사후지하안전영향조사', detail: '20M 이상 굴착' },
    ],
  };

  const currentData = activeTab === 'before' ? beforeConstruction : duringConstruction;

  return (
    <section id="legal-services" className="py-16 sm:py-24 lg:py-32 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-[#ff6b35] text-xs font-semibold tracking-[0.2em] mb-2 sm:mb-3">
            LEGAL &amp; SERVICES
          </div>
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-[#1a2b4a] mb-2">
            건설안전 관련법과 주요업무
          </h2>
          <div className="w-16 sm:w-20 h-1 bg-[#ff6b35] mb-8 sm:mb-12"></div>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex gap-4 sm:gap-8 mb-8 sm:mb-12 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('before')}
            className={`pb-3 sm:pb-4 text-sm sm:text-lg font-semibold transition-all whitespace-nowrap ${
              activeTab === 'before'
                ? 'text-[#1a2b4a] border-b-3 border-[#ff6b35]'
                : 'text-gray-400 border-b-3 border-transparent hover:text-gray-600'
            }`}
          >
            건설공사 착공 전
          </button>
          <button
            onClick={() => setActiveTab('during')}
            className={`pb-3 sm:pb-4 text-sm sm:text-lg font-semibold transition-all whitespace-nowrap ${
              activeTab === 'during'
                ? 'text-[#1a2b4a] border-b-3 border-[#ff6b35]'
                : 'text-gray-400 border-b-3 border-transparent hover:text-gray-600'
            }`}
          >
            건설공사 착공 중
          </button>
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8"
          >
            {/* Labor Law Column */}
            <div>
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#ff6b35]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-text-line text-[#ff6b35] text-lg sm:text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg sm:text-2xl font-bold text-[#1a2b4a]">
                    산업안전보건법
                  </h3>
                  <p className="text-xs sm:text-sm text-[#ff6b35] font-medium">
                    고용노동부
                  </p>
                </div>
              </div>
              <div className="space-y-3 sm:space-y-4">
                {currentData.labor.map((item, index) => (
                  <div
                    key={index}
                    className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5 hover:border-[#ff6b35] transition-colors"
                  >
                    <div className="flex gap-3 sm:gap-4">
                      <div className="w-1 bg-[#ff6b35] rounded-full flex-shrink-0"></div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm sm:text-lg font-bold text-[#1a2b4a] mb-0.5 sm:mb-1">
                          {item.title}
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600">{item.detail}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Construction Law Column */}
            <div>
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#1a2b4a]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-building-line text-[#1a2b4a] text-lg sm:text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg sm:text-2xl font-bold text-[#1a2b4a]">
                    건설기술진흥법
                  </h3>
                  <p className="text-xs sm:text-sm text-[#1a2b4a] font-medium">
                    국토교통부
                  </p>
                </div>
              </div>
              <div className="space-y-3 sm:space-y-4">
                {currentData.construction.map((item, index) => (
                  <div
                    key={index}
                    className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5 hover:border-[#1a2b4a] transition-colors"
                  >
                    <div className="flex gap-3 sm:gap-4">
                      <div className="w-1 bg-[#1a2b4a] rounded-full flex-shrink-0"></div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm sm:text-lg font-bold text-[#1a2b4a] mb-0.5 sm:mb-1">
                          {item.title}
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600">{item.detail}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
